import { useState, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  Lock,
  Pencil,
  RotateCcw,
  Tag as TagIcon,
  Trash2,
  Workflow as WorkflowIcon,
} from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  useConfirmDialog,
} from '@/components/ui';
import { useWorkflows } from '@/lib/workflows-store';
import { useActivityTypes } from '@/lib/activity-types-store';
import {
  TIPOS_RESPOSTA,
  useQuestionarioActions,
  useQuestionarioPerguntas,
  useQuestionarioTemplates,
  type CreatePerguntaInput,
  type CreateTemplateInput,
  type QuestionarioPergunta,
  type QuestionarioTemplate,
  type TipoResposta,
} from '@/lib/questionarios-store';
// Bloco D.2: painel "Ações disparadas" no editor de pergunta.
import { AcoesDisparadasPanel } from '@/components/admin/AcoesDisparadasPanel';

/**
 * /admin/questionarios — gestão de questionários estruturados (FEAT-V2-008, Bloco B).
 *
 * Form builder linear (Opção A do handoff). Lista perguntas em sequência,
 * cada uma com chips de tipo / condicional / visibilidade. Modal de edição
 * com painel "Ações disparadas" RESERVADO pro Bloco D (preview disabled).
 *
 * NOTA: drag-and-drop foi adiado pra polish follow-up — reordenação MVP via
 * input numérico de "ordem". DnD será adicionado depois com `@dnd-kit/core`
 * já em uso no Kanban (`KanbanBoard.tsx`).
 *
 * FASE-C: h1 próprio via <PageHeader>, Ativos/Arquivados no <Tabs> canônico
 * (mesmos rótulos/ordem/testids) e copy sem jargão — "template" vira "modelo",
 * "workflow" vira "fluxo de trabalho", emoji de ícone saiu.
 */
export function QuestionariosPage() {
  // BUG-V2-019 / BF.4: toggle Ativos / Arquivados.
  // Lista templates "ativos" (ativo=1) por default; "arquivados" filtra ativo=0.
  const [showArchived, setShowArchived] = useState(false);
  const { data: allTemplates = [] } = useQuestionarioTemplates({
    includeArchived: showArchived,
  });
  // Quando showArchived=true, lista retorna ativos E arquivados — filtra
  // pra mostrar só arquivados.
  const templates = showArchived
    ? allTemplates.filter((t) => !t.ativo)
    : allTemplates.filter((t) => t.ativo);

  const { createTemplate, updateTemplate, deleteTemplate, pending } = useQuestionarioActions();
  const [newTplDialog, setNewTplDialog] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<QuestionarioTemplate | null>(null);

  async function handleDeleteConfirmed() {
    if (!confirmDelete) return;
    try {
      await deleteTemplate(confirmDelete.id);
      setConfirmDelete(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Não foi possível apagar o modelo.');
    }
  }

  async function handleRestore(t: QuestionarioTemplate) {
    try {
      await updateTemplate(t.id, { ativo: true });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Não foi possível restaurar o modelo.');
    }
  }

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        crumb="Configurações · Operação"
        title="Questionários"
        subtitle={`${templates.length} ${templates.length === 1 ? 'modelo' : 'modelos'} ${showArchived ? 'arquivado(s)' : 'em uso'}. Cada modelo é uma lista de perguntas que a equipe responde em campo.`}
        actions={
          <Button
            type="button"
            onClick={() => setNewTplDialog(true)}
            loading={pending === 'create-template'}
            data-testid="questionarios-new-template-btn"
          >
            + Novo modelo
          </Button>
        }
      />

      {/* FASE-C (copy): o alerta era um resumo de ficha técnica ("FEAT-V2-008",
          "form builder", "flag", "audit"). Agora explica o que o admin faz aqui. */}
      <Alert variant="info" rail>
        Um modelo pode ser ligado a um <strong>fluxo de trabalho</strong> — e aí vale pra
        todas as ordens de serviço daquele fluxo — <strong>ou</strong> a um{' '}
        <strong>tipo de tarefa</strong>, valendo só pras ordens desse tipo. Se você mudar um
        modelo que já tem respostas, o sistema guarda a versão anterior: as respostas
        antigas continuam do jeito que foram dadas.
      </Alert>

      {/* BUG-V2-019 / BF.4: Ativos / Arquivados. FASE-C: virou o <Tabs>
          canônico (1 linha rolável no mobile) — mesmos rótulos e testids. */}
      <div data-testid="templates-archive-toggle">
        <Tabs
          value={showArchived ? 'arquivados' : 'ativos'}
          onValueChange={(v) => setShowArchived(v === 'arquivados')}
        >
          <TabsList>
            <TabsTrigger value="ativos" data-testid="templates-toggle-ativos">
              Ativos
            </TabsTrigger>
            <TabsTrigger value="arquivados" data-testid="templates-toggle-arquivados">
              Arquivados
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {templates.length === 0 && (
        <EmptyState
          title={showArchived ? 'Nenhum modelo arquivado' : 'Nenhum modelo ainda'}
          description={
            showArchived
              ? 'Modelos arquivados aparecem aqui e podem ser restaurados a qualquer momento.'
              : 'Crie o primeiro modelo no botão acima. Depois você escolhe se ele vale pra um fluxo de trabalho inteiro ou só pra um tipo de tarefa.'
          }
        />
      )}

      {templates.length > 0 && (
        <div className="space-y-3">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              isExpanded={activeTemplateId === t.id}
              onToggle={() => setActiveTemplateId(activeTemplateId === t.id ? null : t.id)}
              onRequestDelete={() => setConfirmDelete(t)}
              onRestore={showArchived ? () => handleRestore(t) : undefined}
              isPendingRestore={pending === t.id}
            />
          ))}
        </div>
      )}

      {newTplDialog && (
        <NewTemplateDialog
          onClose={() => setNewTplDialog(false)}
          onCreated={(t) => {
            setNewTplDialog(false);
            setActiveTemplateId(t.id);
          }}
          create={createTemplate}
          pending={pending}
        />
      )}

      {confirmDelete && (
        <Dialog open onOpenChange={(o) => !o && setConfirmDelete(null)} size="sm">
          <DialogHeader>
            <DialogTitle>Apagar modelo?</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <p className="text-sm text-ink-1 dark:text-ink-dark-1 mb-2">
              Apagar o modelo <strong>{confirmDelete.nome}</strong>?
            </p>
            <p className="text-xs text-ink-2 dark:text-ink-dark-2">
              Se alguém já respondeu esse modelo, ele vai pra aba Arquivados em vez de ser
              apagado — as respostas antigas continuam guardadas.
            </p>
          </DialogContent>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirmed}
              loading={pending === confirmDelete.id}
              data-testid="confirm-delete-template"
            >
              Apagar
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
}

// ─── Template card ───────────────────────────────────────────────────────

interface TemplateCardProps {
  template: QuestionarioTemplate;
  isExpanded: boolean;
  onToggle: () => void;
  onRequestDelete: () => void;
  /** BF.4: callback Restaurar quando arquivado. Quando undefined, oculta botão. */
  onRestore?: () => void;
  isPendingRestore?: boolean;
}

function TemplateCard({
  template,
  isExpanded,
  onToggle,
  onRequestDelete,
  onRestore,
  isPendingRestore,
}: TemplateCardProps) {
  const isArchived = !template.ativo;
  // Bloco F.1: badge de vinculação. Verde pra workflow, roxo pra activity_type.
  const { data: workflows = [] } = useWorkflows({ ativo: true });
  const workflowName = template.workflowId
    ? workflows.find((w) => w.id === template.workflowId)?.nome ?? template.workflowId
    : null;

  return (
    <Card>
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-lg text-ink-1 dark:text-ink-dark-1">
              {template.nome}
            </h3>
            <Badge variant={template.ativo ? 'success' : 'neutral'} size="sm">
              {template.ativo ? 'Ativo' : 'Inativo'}
            </Badge>
            <Badge variant="info" size="sm">v{template.versao}</Badge>
            {template.deprecatedEmIso && (
              <Badge variant="warning" size="sm">Substituído</Badge>
            )}
            {/* Badge de vinculação. FASE-C: emoji ⚙️/🏷️ viraram ícone Lucide. */}
            {workflowName && (
              <Badge variant="success" size="sm" data-testid={`vinc-workflow-${template.id}`}>
                <WorkflowIcon className="w-3 h-3" aria-hidden="true" />
                Fluxo: {workflowName}
              </Badge>
            )}
            {template.activityType && (
              <Badge variant="danger" size="sm" data-testid={`vinc-type-${template.id}`}>
                <TagIcon className="w-3 h-3" aria-hidden="true" />
                Tipo: {template.activityType.label}
              </Badge>
            )}
            {!template.workflowId && !template.activityTypeId && (
              <Badge variant="info" size="sm" data-testid={`vinc-os-${template.id}`}>
                Ordens de serviço
              </Badge>
            )}
          </div>
          {template.descricao && (
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">{template.descricao}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggle}
            aria-expanded={isExpanded}
            data-testid={`template-toggle-${template.id}`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 mr-1" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-4 h-4 mr-1" aria-hidden="true" />
            )}
            {isExpanded ? 'Recolher' : 'Gerenciar perguntas'}
          </Button>
          {/* BF.4: Restaurar quando arquivado. Excluir oculto pra arquivados (já estão). */}
          {isArchived && onRestore ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onRestore}
              loading={isPendingRestore}
              data-testid={`template-restore-${template.id}`}
              aria-label="Restaurar modelo"
            >
              <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" />
              Restaurar
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRequestDelete}
              data-testid={`template-delete-${template.id}`}
              aria-label="Arquivar modelo"
              title="Arquivar modelo"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-line dark:border-line-dark p-4">
          <PerguntasManager templateId={template.id} deprecated={!!template.deprecatedEmIso} />
        </div>
      )}
    </Card>
  );
}

// ─── Perguntas manager ──────────────────────────────────────────────────

interface PerguntasManagerProps {
  templateId: string;
  deprecated: boolean;
}

function PerguntasManager({ templateId, deprecated }: PerguntasManagerProps) {
  // FASE 2 / M1: ConfirmDialog do design system (substitui confirm() nativo)
  const confirm = useConfirmDialog();
  const { data: perguntas = [], isLoading } = useQuestionarioPerguntas(templateId);
  const actions = useQuestionarioActions();
  const [perguntaDialogOpen, setPerguntaDialogOpen] = useState(false);
  const [editingPergunta, setEditingPergunta] = useState<QuestionarioPergunta | null>(null);

  if (deprecated) {
    return (
      <Alert variant="warning" rail>
        Este modelo foi substituído por uma versão mais nova. Edite as perguntas na
        versão que está em uso.
      </Alert>
    );
  }

  return (
    <div className="min-w-0 space-y-3">
      {isLoading && (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">Carregando perguntas…</p>
      )}

      {!isLoading && perguntas.length === 0 && (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">Nenhuma pergunta ainda.</p>
      )}

      {perguntas.map((p) => (
        <PerguntaRow
          key={p.id}
          pergunta={p}
          onEdit={() => {
            setEditingPergunta(p);
            setPerguntaDialogOpen(true);
          }}
          onDelete={async () => {
            const ok = await confirm({
              title: 'Remover pergunta',
              message: `Remover a pergunta ${p.codigo}? Se este modelo já tem respostas, o sistema cria uma versão nova e preserva as antigas.`,
              confirmLabel: 'Remover',
              variant: 'destructive',
            });
            if (!ok) return;
            const r = await actions.deletePergunta(p.id);
            if (r.versionedTemplateId) {
              alert(
                'A mudança entrou numa versão nova do modelo. Atualize a página pra ver o resultado.',
              );
            }
          }}
        />
      ))}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          setEditingPergunta(null);
          setPerguntaDialogOpen(true);
        }}
        data-testid={`add-pergunta-${templateId}`}
      >
        + Adicionar pergunta
      </Button>

      {perguntaDialogOpen && (
        <PerguntaDialog
          templateId={templateId}
          editing={editingPergunta}
          siblings={perguntas}
          onClose={() => {
            setPerguntaDialogOpen(false);
            setEditingPergunta(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Pergunta row ────────────────────────────────────────────────────────

interface PerguntaRowProps {
  pergunta: QuestionarioPergunta;
  onEdit: () => void;
  onDelete: () => void;
}

function PerguntaRow({ pergunta, onEdit, onDelete }: PerguntaRowProps) {
  return (
    <div
      className="flex items-start gap-3 p-3 border border-line dark:border-line-dark rounded-lg"
      data-testid={`pergunta-row-${pergunta.codigo}`}
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-paper-warm dark:bg-paper-dark-warm flex items-center justify-center text-xs font-medium text-ink-1 dark:text-ink-dark-1">
        {pergunta.ordem}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
          <Badge variant="info" size="sm">{pergunta.codigo}</Badge>
          <Badge variant="neutral" size="sm">{labelTipo(pergunta.tipoResposta)}</Badge>
          {pergunta.condicionalPerguntaCodigo && (
            <Badge variant="warning" size="sm">
              {/* FASE-C: o glifo ↳ virou ícone Lucide. */}
              <CornerDownRight className="w-3 h-3" aria-hidden="true" />
              Só se {pergunta.condicionalPerguntaCodigo}
            </Badge>
          )}
          {!pergunta.isPublic && (
            <Badge variant="danger" size="sm">
              <Lock className="w-3 h-3" aria-hidden="true" />
              Interna
            </Badge>
          )}
          {pergunta.obrigatoria && <Badge variant="neutral" size="sm">Obrigatória</Badge>}
        </div>
        <p className="text-sm text-ink-1 dark:text-ink-dark-1 break-words">
          {pergunta.pergunta}
        </p>
        {pergunta.opcoes.length > 0 && (
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1 break-words">
            Opções: {pergunta.opcoes.join(', ')}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-col gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          aria-label={`Editar a pergunta ${pergunta.codigo}`}
          title="Editar"
        >
          <Pencil className="w-4 h-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          aria-label={`Remover a pergunta ${pergunta.codigo}`}
          title="Remover"
        >
          <Trash2 className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

// ─── Pergunta dialog (criação + edição) ─────────────────────────────────

interface PerguntaDialogProps {
  templateId: string;
  editing: QuestionarioPergunta | null;
  siblings: QuestionarioPergunta[];
  onClose: () => void;
}

function PerguntaDialog({ templateId, editing, siblings, onClose }: PerguntaDialogProps) {
  const actions = useQuestionarioActions();
  const [codigo, setCodigo] = useState(editing?.codigo ?? '');
  const [pergunta, setPergunta] = useState(editing?.pergunta ?? '');
  const [tipoResposta, setTipoResposta] = useState<TipoResposta>(editing?.tipoResposta ?? 'sim_nao');
  const [opcoesText, setOpcoesText] = useState((editing?.opcoes ?? []).join('\n'));
  const [obrigatoria, setObrigatoria] = useState(editing?.obrigatoria ?? true);
  const [isPublic, setIsPublic] = useState(editing?.isPublic ?? true);
  const [ordem, setOrdem] = useState(editing?.ordem ?? siblings.length + 1);
  const [condicionalCodigo, setCondicionalCodigo] = useState(
    editing?.condicionalPerguntaCodigo ?? ''
  );
  const [condicionalValor, setCondicionalValor] = useState(
    editing?.condicionalValor !== undefined && editing.condicionalValor !== null
      ? String(editing.condicionalValor)
      : ''
  );
  const [error, setError] = useState<string | null>(null);

  const needsOpcoes = tipoResposta === 'multipla_escolha' || tipoResposta === 'unica_escolha';
  const showCondicional = !!condicionalCodigo;

  // Siblings disponíveis pra condicional (exclui a própria pergunta sendo editada)
  const possiveisCondicionais = siblings.filter((s) => !editing || s.id !== editing.id);

  async function handleSave() {
    setError(null);
    if (!codigo.trim()) return setError('Código obrigatório (ex: P1, P2)');
    if (!pergunta.trim()) return setError('Texto da pergunta obrigatório');
    if (needsOpcoes && !opcoesText.trim()) {
      return setError('Tipos múltipla/única escolha exigem ao menos uma opção');
    }

    const opcoes = opcoesText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    const input: CreatePerguntaInput = {
      codigo: codigo.trim().toUpperCase(),
      pergunta: pergunta.trim(),
      tipoResposta,
      opcoes,
      ordem,
      obrigatoria,
      isPublic,
      condicionalPerguntaCodigo: condicionalCodigo || null,
      condicionalValor: condicionalCodigo && condicionalValor ? condicionalValor : null,
    };

    try {
      if (editing) {
        const r = await actions.updatePergunta(editing.id, input);
        if (r.versionedTemplateId) {
          alert(
            'A mudança entrou numa versão nova do modelo — as respostas antigas ficaram na versão anterior.',
          );
        }
      } else {
        const r = await actions.createPergunta(templateId, input);
        if (r.versionedTemplateId) {
          alert(
            'A pergunta entrou numa versão nova do modelo — as respostas antigas ficaram na versão anterior.',
          );
        }
      }
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Não foi possível salvar a pergunta.';
      if (msg.includes('conditional-cycle')) {
        setError('Essas perguntas ficariam dependendo uma da outra em círculo. Escolha outra pergunta na condição.');
      } else if (msg.includes('codigo-conflict')) {
        setError(`Já existe uma pergunta com o código "${codigo}" neste modelo.`);
      } else if (msg.includes('conditional-target-not-found')) {
        setError('A pergunta escolhida na condição não existe mais neste modelo.');
      } else {
        setError(msg);
      }
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>{editing ? `Editar ${editing.codigo}` : 'Nova pergunta'}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          {error && <Alert variant="danger" rail>{error}</Alert>}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="min-w-0">
              <Label htmlFor="p-codigo" required>Código</Label>
              <Input
                id="p-codigo"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="P1"
                data-testid="pergunta-codigo"
              />
            </div>
            <div className="min-w-0 sm:col-span-2">
              <Label htmlFor="p-ordem">Ordem</Label>
              <Input
                id="p-ordem"
                type="number"
                min={1}
                max={9999}
                value={ordem}
                onChange={(e) => setOrdem(Number(e.target.value))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="p-texto" required>Pergunta</Label>
            <Input
              id="p-texto"
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              placeholder="Necessidade de orçamento?"
              data-testid="pergunta-texto"
            />
          </div>

          <div>
            <Label htmlFor="p-tipo" required>Tipo de resposta</Label>
            <Select
              id="p-tipo"
              value={tipoResposta}
              onChange={(e) => setTipoResposta(e.target.value as TipoResposta)}
              data-testid="pergunta-tipo"
            >
              {TIPOS_RESPOSTA.map((t) => (
                <option key={t} value={t}>{labelTipo(t)}</option>
              ))}
            </Select>
          </div>

          {needsOpcoes && (
            <div>
              <Label htmlFor="p-opcoes" required>Opções (uma por linha)</Label>
              <textarea
                id="p-opcoes"
                value={opcoesText}
                onChange={(e) => setOpcoesText(e.target.value)}
                rows={4}
                className="w-full border border-line dark:border-line-dark rounded-lg px-3 py-2 text-sm bg-paper-surface dark:bg-paper-dark-surface"
                placeholder="Opção A&#10;Opção B&#10;Opção C"
                data-testid="pergunta-opcoes"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-4 pt-2">
            <Switch
              checked={obrigatoria}
              onChange={(e) => setObrigatoria(e.target.checked)}
              label="Obrigatória"
              data-testid="pergunta-obrigatoria"
            />
            <Switch
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              label={isPublic ? 'Pública' : 'Interna'}
              hint={
                isPublic
                  ? 'O cliente vê esta pergunta e a resposta.'
                  : 'Só a equipe vê esta pergunta.'
              }
              data-testid="pergunta-is-public"
            />
          </div>

          <Card className="bg-paper-warm dark:bg-paper-dark-warm">
            <div className="p-3 space-y-3">
              <div className="text-xs font-medium text-ink-2 dark:text-ink-dark-2 uppercase tracking-wider">
                Condicional (opcional)
              </div>
              <div>
                <Label htmlFor="p-cond-codigo">Mostrar só se</Label>
                <Select
                  id="p-cond-codigo"
                  value={condicionalCodigo}
                  onChange={(e) => setCondicionalCodigo(e.target.value)}
                  data-testid="pergunta-condicional-codigo"
                >
                  <option value="">— Sempre mostrar —</option>
                  {possiveisCondicionais.map((s) => (
                    <option key={s.id} value={s.codigo}>
                      {s.codigo}: {s.pergunta.slice(0, 50)}
                    </option>
                  ))}
                </Select>
              </div>
              {showCondicional && (
                <div>
                  <Label htmlFor="p-cond-valor">tiver valor</Label>
                  <Input
                    id="p-cond-valor"
                    value={condicionalValor}
                    onChange={(e) => setCondicionalValor(e.target.value)}
                    placeholder="Sim"
                    data-testid="pergunta-condicional-valor"
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Bloco D (preview disabled) */}
          {/* Bloco D.2: painel "Ações disparadas" — lista automations existentes
              filtradas por (templateId, perguntaCodigo). Criar/editar feito no
              admin de automações (link no painel). Só exibe quando pergunta tem
              código definido (caso edit OU after typing). */}
          {editing && codigo && (
            <AcoesDisparadasPanel
              templateId={templateId}
              perguntaCodigo={codigo}
            />
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          onClick={handleSave}
          loading={actions.pending !== null}
          data-testid="pergunta-save-btn"
        >
          {editing ? 'Salvar' : 'Adicionar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── New template dialog ────────────────────────────────────────────────

interface NewTemplateDialogProps {
  onClose: () => void;
  onCreated: (t: QuestionarioTemplate) => void;
  create: (input: CreateTemplateInput) => Promise<QuestionarioTemplate>;
  pending: string | null;
}

type VinculoMode = 'os' | 'workflow' | 'activity_type';

function NewTemplateDialog({ onClose, onCreated, create, pending }: NewTemplateDialogProps) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  // Bloco F.1: vinculação — radios mutex. 🆕 26/10/2026 (Matheus: "ajusta a criação do questionário para não exigir fluxo
  // ou tipo de tarefa quando ele for usado na OS"): "Nas ordens de serviço" é o padrão e não pede nada — o modelo se liga
  // pelo serviço, em Configurações › Ordens de serviço. Fluxo e tipo de tarefa são do módulo antigo.
  const [vinculoMode, setVinculoMode] = useState<VinculoMode>('os');
  const [workflowId, setWorkflowId] = useState<string>('');
  const [activityTypeId, setActivityTypeId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const { data: workflows = [] } = useWorkflows({ ativo: true });
  const { data: activityTypes = [] } = useActivityTypes({ includeInactive: false });

  async function handleSave() {
    if (!nome.trim()) return setError('Dê um nome pro modelo.');
    if (vinculoMode === 'workflow' && !workflowId) {
      return setError('Escolha o fluxo de trabalho onde este modelo vale.');
    }
    if (vinculoMode === 'activity_type' && !activityTypeId) {
      return setError('Escolha o tipo de tarefa onde este modelo vale.');
    }
    try {
      const input: CreateTemplateInput = {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        workflowId: vinculoMode === 'workflow' ? workflowId : null,
        activityTypeId: vinculoMode === 'activity_type' ? activityTypeId : null,
      };
      const t = await create(input);
      onCreated(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível criar o modelo.');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>Novo modelo de questionário</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          {error && <Alert variant="danger" rail>{error}</Alert>}

          <div>
            <Label htmlFor="tpl-nome" required>Nome</Label>
            <Input
              id="tpl-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Preventiva CFTV"
              data-testid="new-template-nome"
            />
          </div>

          <div>
            <Label htmlFor="tpl-desc">Descrição</Label>
            <Input
              id="tpl-desc"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: checklist da preventiva mensal"
            />
          </div>

          {/* Vinculação — radios mutex (Bloco F.1); "Nas ordens de serviço" é o padrão (26/10/2026). */}
          <div className="space-y-2 pt-1">
            <Label>Onde este modelo vale</Label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="vinculo-mode"
                  value="os"
                  className="mt-1 shrink-0"
                  checked={vinculoMode === 'os'}
                  onChange={() => {
                    setVinculoMode('os');
                    setWorkflowId('');
                    setActivityTypeId('');
                  }}
                  data-testid="vinculo-radio-os"
                />
                <span className="min-w-0">
                  Nas <strong>ordens de serviço</strong> — depois de criar, escolha em quais serviços ele vale, em
                  Configurações › Ordens de serviço
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="vinculo-mode"
                  value="workflow"
                  className="mt-1 shrink-0"
                  checked={vinculoMode === 'workflow'}
                  onChange={() => {
                    setVinculoMode('workflow');
                    setActivityTypeId('');
                  }}
                  data-testid="vinculo-radio-workflow"
                />
                <span className="min-w-0">
                  Num <strong>fluxo de trabalho</strong> — vale pra todas as ordens de
                  serviço daquele fluxo
                </span>
              </label>
              {vinculoMode === 'workflow' && (
                <Select
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  data-testid="vinculo-select-workflow"
                  className="sm:ml-6"
                >
                  <option value="">— escolha o fluxo —</option>
                  {workflows.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.nome} — {w.deptSlug}
                    </option>
                  ))}
                </Select>
              )}

              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="vinculo-mode"
                  value="activity_type"
                  className="mt-1 shrink-0"
                  checked={vinculoMode === 'activity_type'}
                  onChange={() => {
                    setVinculoMode('activity_type');
                    setWorkflowId('');
                  }}
                  data-testid="vinculo-radio-type"
                />
                <span className="min-w-0">
                  Num <strong>tipo de tarefa</strong> — vale só pras ordens de serviço
                  desse tipo
                </span>
              </label>
              {vinculoMode === 'activity_type' && (
                <Select
                  value={activityTypeId}
                  onChange={(e) => setActivityTypeId(e.target.value)}
                  data-testid="vinculo-select-type"
                  className="sm:ml-6"
                >
                  <option value="">— escolha o tipo —</option>
                  {activityTypes.map((at) => (
                    <option key={at.id} value={at.id}>
                      {at.label}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          onClick={handleSave}
          loading={pending === 'create-template'}
          data-testid="new-template-save"
        >
          Criar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

const TIPO_LABELS: Record<TipoResposta, string> = {
  sim_nao: 'Sim/Não',
  texto_curto: 'Texto curto',
  texto_longo: 'Texto longo',
  numero: 'Número',
  multipla_escolha: 'Múltipla escolha',
  unica_escolha: 'Única escolha',
  rating_1_5: 'Avaliação 1-5',
  data_hora: 'Data e hora',
  foto: 'Foto',
  assinatura_inline: 'Assinatura',
  anexo: 'Anexo',
};

function labelTipo(t: TipoResposta): ReactNode {
  return TIPO_LABELS[t] ?? t;
}
